import React from 'react';
import styled from 'styled-components';

import {
  Box,
  Card,
  CardContent as MuiCardContent,
  Chip as MuiChip,
  Typography as MuiTypography,
  Tooltip,
} from '@mui/material';

import { rgba } from 'polished';

import { spacing } from '@mui/system';

const Typography = styled(MuiTypography)(spacing);

const CardContent = styled(MuiCardContent)`
  position: relative;

  &:last-child {
    padding-bottom: ${(props) => props.theme.spacing(4)}px;
  }
`;

const Chip = styled(MuiChip)`
  position: absolute;
  top: 9px;
  right: 16px;
  height: 20px;
  padding: 4px 0;
  font-size: 85%;
  background-color: ${(props) => props.theme.palette.action.selected};
  color: ${(props) => props.theme.palette.text.primary};
  margin-bottom: ${(props) => props.theme.spacing(4)};

  span {
    padding-left: ${(props) => props.theme.spacing(2)};
    padding-right: ${(props) => props.theme.spacing(2)};
  }
`;

const Percentage = styled(MuiTypography)<{
  percentagecolor: string;
  mb: number;
}>`
  display: flex;
  align-items: center;

  /* Style the entire content wrapper */
  & > * {
    color: ${(props) => {
      // Map legacy color names to theme palette
      if (props.percentagecolor === 'green') return props.theme.palette.success.main;
      if (props.percentagecolor === 'red') return props.theme.palette.error.main;
      return props.percentagecolor;
    }} !important;
    font-weight: ${(props) => props.theme.typography.fontWeightBold};
  }

  /* Background highlight for the percentage text */
  span {
    color: ${(props) => {
      // Map legacy color names to theme palette
      if (props.percentagecolor === 'green') return props.theme.palette.success.main;
      if (props.percentagecolor === 'red') return props.theme.palette.error.main;
      return props.percentagecolor;
    }} !important;
    font-weight: ${(props) => props.theme.typography.fontWeightBold};
    background: ${(props) => {
      const color =
        props.percentagecolor === 'green'
          ? props.theme.palette.success.main
          : props.percentagecolor === 'red'
            ? props.theme.palette.error.main
            : props.percentagecolor;
      return rgba(color, 0.1);
    }};
    padding: 2px 6px;
    border-radius: 3px;
    margin-right: ${(props) => props.theme.spacing(2)};
  }
`;

type StatsPropsType = {
  title: JSX.Element;
  amount: string | number;
  amountTooltip?: JSX.Element;
  chip: string;
  percentageText?: JSX.Element;
  percentageTextPrefix?: string;
  percentagecolor?: string;
  percentageTooltip?: JSX.Element;
  sinceText?: string;
};

const Stats: React.FC<StatsPropsType> = ({
  title,
  amount,
  amountTooltip,
  chip,
  percentageText,
  percentagecolor = 'green',
  percentageTextPrefix,
  percentageTooltip,
  sinceText,
}) => {
  return (
    <Card
      elevation={0}
      sx={{
        backgroundColor: (theme) =>
          theme.palette.mode === 'dark' ? 'rgba(15, 23, 42, 0.85)' : 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(24px)',
        border: (theme) =>
          theme.palette.mode === 'dark'
            ? '1px solid rgba(148, 163, 184, 0.12)'
            : '1px solid rgba(226, 232, 240, 0.8)',
        borderRadius: '16px',
        boxShadow: (theme) =>
          theme.palette.mode === 'dark'
            ? '0 4px 24px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.05) inset'
            : '0 4px 24px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          boxShadow: (theme) =>
            theme.palette.mode === 'dark'
              ? '0 8px 32px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.08) inset'
              : '0 8px 32px rgba(0, 0, 0, 0.08), 0 2px 4px rgba(0, 0, 0, 0.04)',
        },
      }}
    >
      <CardContent>
        <Typography variant="h6" sx={{ mb: 6 }}>
          {title}
        </Typography>
        <Typography variant="h3" sx={{ mb: 6 }}>
          <Tooltip title={amountTooltip ?? ''}>
            <span>
              <Box width="fit-content">{amount}</Box>
            </span>
          </Tooltip>
        </Typography>
        {percentageText && (
          <Tooltip title={percentageTooltip ?? ''}>
            <span>
              <Box display="flex" gap={1} alignItems="center">
                {percentageTextPrefix && (
                  <Box component={'span'} sx={{ color: (theme) => theme.palette.text.primary }}>
                    {percentageTextPrefix}
                  </Box>
                )}
                <Percentage
                  variant="subtitle2"
                  mb={6}
                  color="textSecondary"
                  percentagecolor={percentagecolor || ''}
                >
                  <span>{percentageText}</span> {sinceText}
                </Percentage>
              </Box>
            </span>
          </Tooltip>
        )}
        <Chip label={chip} />
      </CardContent>
    </Card>
  );
};

export default Stats;
